import { useState } from "react";
import { useAuth, API, safeStorage } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Play, RefreshCw, LogOut, User, ShieldCheck } from "lucide-react";

/**
 * Rumeni trak, ki opozarja da gre za demo sejo.
 * Omogoča preklop vloge (član ↔ predsednik) in ročni reset podatkov.
 */
export const DemoBanner = () => {
  const { user, token, logout, login } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const [resetting, setResetting] = useState(false);

  if (!user?.is_demo) return null;

  const currentRole = user.vloga === "predsednik" ? "predsednik" : "clan";
  const otherRole = currentRole === "predsednik" ? "clan" : "predsednik";

  const switchRole = async () => {
    setSwitching(true);
    try {
      const r = await axios.post(`${API}/demo/switch-role`, { role: otherRole }, { headers: { Authorization: `Bearer ${token}` } });
      const { token: newToken, user: userData } = r.data;
      safeStorage.setItem("token", newToken);
      if (userData?.ld_id) safeStorage.setItem("ld_id", userData.ld_id);
      if (userData?.ld_naziv) safeStorage.setItem("ld_naziv", userData.ld_naziv);
      await login("", "", null, { skipRequest: true, prefetched: { token: newToken, user: userData } });
      toast.success(`Preklopljeno v vlogo: ${otherRole === "predsednik" ? "predsednik" : "član"}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri preklopu vloge");
    } finally { setSwitching(false); }
  };

  const resetData = async () => {
    if (!window.confirm("Resetiraj demo podatke na začetno stanje?")) return;
    setResetting(true);
    try {
      await axios.post(`${API}/demo/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Demo podatki resetirani");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri resetu");
    } finally { setResetting(false); }
  };

  const exitDemo = () => {
    logout();
    navigate("/login");
  };

  return (
    <div
      className="sticky top-0 z-[60] w-full bg-amber-500 dark:bg-amber-600 text-black dark:text-white shadow-md"
      data-testid="demo-banner"
    >
      <div className="max-w-full mx-auto px-3 py-1.5 flex items-center gap-2 flex-wrap text-xs sm:text-sm">
        <Play className="h-4 w-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <b>Demo način:</b> pregledujete testne podatke ({currentRole === "predsednik" ? "predsednik" : "član"}).
          <span className="hidden sm:inline"> Spremembe se občasno avtomatsko resetirajo.</span>
        </div>
        <button
          onClick={switchRole}
          disabled={switching}
          className="inline-flex items-center gap-1 bg-black/15 hover:bg-black/25 px-2 py-1 rounded text-xs font-medium transition"
          data-testid="demo-switch-role"
          title={`Preklopi na ${otherRole}`}
        >
          {otherRole === "predsednik" ? <ShieldCheck className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">
            {switching ? "Preklapljam…" : `→ ${otherRole === "predsednik" ? "predsednik" : "član"}`}
          </span>
        </button>
        <button
          onClick={resetData}
          disabled={resetting}
          className="inline-flex items-center gap-1 bg-black/15 hover:bg-black/25 px-2 py-1 rounded text-xs font-medium transition"
          data-testid="demo-reset"
          title="Resetiraj demo podatke"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${resetting ? "animate-spin" : ""}`} />
          <span className="hidden md:inline">Reset</span>
        </button>
        <button
          onClick={exitDemo}
          className="inline-flex items-center gap-1 bg-black/15 hover:bg-black/25 px-2 py-1 rounded text-xs font-medium transition"
          data-testid="demo-exit"
          title="Izhod iz demo načina"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Izhod</span>
        </button>
      </div>
    </div>
  );
};

export default DemoBanner;
